/**
 * Tracks slow API calls and persists them to localStorage so you can
 * run through the app and collect a list of endpoints to optimize.
 *
 * Enable with VITE_TRACK_SLOW_API=true or use in development (auto-enabled in DEV).
 */

const STORAGE_KEY = 'jema_slow_api_calls';

/** Default threshold in ms; overridable via VITE_SLOW_API_THRESHOLD_MS */
const DEFAULT_THRESHOLD_MS = 2000;

export interface SlowApiEntry {
  /** API path (e.g. /api/v2/patients/123/stats) */
  path: string;
  /** HTTP method */
  method: string;
  /** Last observed duration in ms */
  lastDurationMs: number;
  /** Max duration seen in ms */
  maxDurationMs: number;
  /** Number of times this endpoint was seen slow */
  count: number;
  /** Last time we saw this (ISO string) */
  lastSeen: string;
}

function isEnabled(): boolean {
  if (typeof import.meta.env?.DEV === 'boolean' && import.meta.env.DEV) return true;
  return import.meta.env?.VITE_TRACK_SLOW_API === 'true';
}

function getThresholdMs(): number {
  const raw = import.meta.env?.VITE_SLOW_API_THRESHOLD_MS;
  if (raw == null || raw === '') return DEFAULT_THRESHOLD_MS;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_THRESHOLD_MS;
}

function loadEntries(): SlowApiEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SlowApiEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveEntries(entries: SlowApiEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // ignore quota or other storage errors
  }
}

function key(path: string, method: string): string {
  return `${method}\t${path}`;
}

/**
 * Record a slow API call. Call this when a request exceeds the threshold.
 * Merges by path+method (updates count, max duration, last seen).
 */
export function recordSlowApi(path: string, method: string, durationMs: number): void {
  if (!isEnabled()) return;

  const threshold = getThresholdMs();
  if (durationMs < threshold) return;

  const entries = loadEntries();
  const k = key(path, method);
  const existingIndex = entries.findIndex(e => key(e.path, e.method) === k);

  const entry: SlowApiEntry = {
    path,
    method: method || 'GET',
    lastDurationMs: durationMs,
    maxDurationMs: durationMs,
    count: 1,
    lastSeen: new Date().toISOString(),
  };

  if (existingIndex >= 0) {
    const prev = entries[existingIndex];
    entry.count = prev.count + 1;
    entry.maxDurationMs = Math.max(prev.maxDurationMs, durationMs);
    entries[existingIndex] = entry;
  } else {
    entries.push(entry);
  }

  saveEntries(entries);
}

/**
 * Returns the current list of slow API entries, sorted by max duration descending.
 */
export function getSlowApiCalls(): SlowApiEntry[] {
  const entries = loadEntries();
  return [...entries].sort((a, b) => b.maxDurationMs - a.maxDurationMs);
}

/**
 * Clears the persisted list of slow API calls.
 */
export function clearSlowApiCalls(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Threshold in ms above which a request is considered slow.
 */
export function getSlowApiThresholdMs(): number {
  return getThresholdMs();
}
